/**
 * punchBagPipeline — a hanging teardrop form that swings and dimples
 * where bass hits it. Vertical displacement dominates, with sway
 * driven by accumulated bass impulse and damped by the mid band.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface PunchBagPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(443.897, 441.423));
  p += dot(p, p + 19.19);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1, 0));
  float c = hash21(i + vec2(0, 1));
  float d = hash21(i + vec2(1, 1));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm2(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.03 + vec2(1.7, -3.1);
    a *= 0.5;
  }
  return v;
}

// Hanging teardrop — top is narrower than bottom.
float teardrop(vec3 p) {
  // Sway the entire body laterally based on bass impulse.
  float t = uTime;
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  bass = pow(clamp(bass * 2.4, 0.0, 1.0), 0.65);
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  mid = pow(clamp(mid * 2.6, 0.0, 1.0), 0.8);
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  high = pow(clamp(high * 3.6, 0.0, 1.0), 0.85);
  // Body sway: pendulum with multiple bass sub-modes.
  vec3 sp = p;
  float sway = sin(t * 0.7) * uBass.x * 0.35 + sin(t * 0.43 + 1.7) * uBass.y * 0.28 + sin(t * 0.31 - 0.9) * uBass.z * 0.22;
  sway *= 1.0 - 0.35 * mid; // mid acts as pendulum damping
  sp.x -= sway;
  // Vertical taper — narrower at the top.
  float taper = 0.45 + (sp.y + 1.2) * 0.32;
  taper = clamp(taper, 0.35, 0.85);
  vec2 q = vec2(sp.x / taper, sp.z / taper);
  float r = length(q);
  float body = r - (0.85 + 0.05 * sin(t * 0.4));
  // Bottom bulb.
  float bulb = length(vec2(sp.x / 1.05, (sp.y + 0.05) / 1.15)) - 0.95;
  float d = max(body, bulb);
  // Punch dimples — three orbital impacts.
  vec3 c1 = vec3(0.5 * cos(t * 0.5), 0.4 * sin(t * 0.4), 0.95);
  vec3 c2 = vec3(0.5 * sin(t * 0.45 + 2.0), -0.2 * cos(t * 0.3), 0.95);
  vec3 c3 = vec3(-0.4 * cos(t * 0.6 - 1.0), 0.1 * sin(t * 0.55), 0.95);
  d -= uBass.x * exp(-dot(sp - c1, sp - c1) * 5.0) * 0.32;
  d -= uBass.y * exp(-dot(sp - c2, sp - c2) * 5.0) * 0.32;
  d -= uBass.z * exp(-dot(sp - c3, sp - c3) * 5.0) * 0.32;
  // Mid creates surface ripple bands.
  d += sin(atan(sp.x, sp.z) * 12.0 + t * 2.0) * 0.015 * mid;
  // High micro-bumps.
  d += (fbm2(sp.xy * 14.0 + vec2(t * 0.6, 0.0)) - 0.5) * 0.012 * high;
  return d;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(
    teardrop(p + e.xyy) - teardrop(p - e.xyy),
    teardrop(p + e.yxy) - teardrop(p - e.yxy),
    teardrop(p + e.yyx) - teardrop(p - e.yyx)
  ));
}

void main() {
  vec2 uv = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  uv.x *= uResolution.x / uResolution.y;
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.4, 0.0, 1.0), 0.65);
  mid = pow(clamp(mid * 2.6, 0.0, 1.0), 0.8);
  high = pow(clamp(high * 3.6, 0.0, 1.0), 0.85);

  vec3 ro = vec3(0.0, 0.0, 3.6);
  vec3 rd = normalize(vec3(uv, -1.6));

  float t = 0.0;
  bool hit = false;
  vec3 p = ro;
  for (int i = 0; i < 100; i++) {
    float d = teardrop(p);
    if (d < 0.001) { hit = true; break; }
    if (t > 8.0) break;
    t += d * 0.9;
    p = ro + rd * t;
  }
  vec3 col = vec3(0.05, 0.04, 0.06);
  if (hit) {
    vec3 n = calcNormal(p);
    vec3 ldir = normalize(vec3(0.6, 0.8, 0.4));
    float diff = clamp(dot(n, ldir), 0.0, 1.0);
    float fres = pow(1.0 - clamp(dot(n, -rd), 0.0, 1.0), 2.5);
    vec3 h = normalize(ldir - rd);
    float spec = pow(clamp(dot(n, h), 0.0, 1.0), 40.0);
    // Leather grain — high-frequency stripes in the horizontal direction.
    float grain = fbm2(vec2(p.y * 30.0, p.x * 12.0 + p.z * 8.0));
    col = vec3(0.0);
    col += diff * mix(vec3(0.55, 0.30, 0.20), vec3(0.85, 0.55, 0.35), grain);
    col += fres * vec3(0.95, 0.6, 0.45) * (0.3 + high * 0.8);
    col += spec * vec3(1.0) * (0.4 + bass * 0.7);
    // Strap shadow at the top.
    float strap = smoothstep(0.85, 1.2, p.y);
    col *= 1.0 - strap * 0.6;
  }

  float vig = pow(16.0 * vUV.x * (1.0 - vUV.x) * vUV.y * (1.0 - vUV.y), 0.25);
  col *= vig;
  col = col / (1.0 + col);
  col = pow(col, vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}`;

export function computePunchBagBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.9);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.7));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.5);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.1 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.7 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.5 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 4.0 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 6.0 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 8.0 + 2.1);
}

export function computePunchBagRealtimeBands(freqData: Uint8Array, out: Float32Array): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.008], [0.008, 0.030], [0.030, 0.060],
    [0.060, 0.150], [0.150, 0.260], [0.260, 0.420],
    [0.420, 0.620], [0.620, 0.800], [0.800, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    let s = 0, c = 0;
    const a = Math.floor(lo * N);
    const b = Math.floor(hi * N);
    for (let k = a; k < b; k++) { s += freqData[k]; c++; }
    out[i] = c > 0 ? (s / c) / 255 : 0;
  }
}

export function createPunchBagPipeline(gl: WebGL2RenderingContext): PunchBagPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawPunchBag(
  gl: WebGL2RenderingContext,
  pipe: PunchBagPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}